<script setup lang="ts">
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

/**
 * ⭐ „One conversation, two rooms" — die Relay-Story als Palindrom:
 *
 *   Single (Channel, zentriert) → Split (DM links | Forum rechts) → Single
 *   (DM mit Closing-Karte). Die Discord-Fenster bleiben 1:1 authentisch —
 *   alles Flashy passiert ZWISCHEN ihnen.
 *
 * Der Ablauf ist der ECHTE Ticket-Flow des Bots (DM-Tickets, Mirroring an):
 *   1. Panel im #support-Channel: CREATION-Karte mit einem Button pro Kategorie
 *   2. Klick → Modal, Titel = Kategorie, Felder = die Inputs der Kategorie
 *      (hier der vorgefertigte Stripe-Input + eine Freitextfrage)
 *   3. DM: OPENING-Karte („Ticket Opened") · Forum: neuer Post „jonas.-Billing"
 *      mit der STARTED-Karte (Inputs als Name → Wert) + Close/Assign
 *   4. Stripe-Lookup läuft beim Öffnen — die Karte landet im Thread
 *   5. Team antwortet im Thread → Bot relayt in die DM; Member antwortet in der
 *      DM → landet per Webhook unter seinem Namen im Thread
 *   6. Close → CLOSED-Karte mit Reopen / Transcript / Feedback, Thread archiviert
 *
 * Bot-Karten sind Components-v2-Container (DiscordCard), keine klassischen
 * Embeds — so postet der Bot wirklich. Texte folgen den Default-Blueprints.
 *
 * Autoplay statt Pin: Die Timeline läuft zeitbasiert (~48 s Timeline-Zeit,
 * per timeScale 1.7 ≈ 28 s real, Loop) und wird
 * von einem ScrollTrigger nur gestartet/pausiert — der Seiten-Scroll bleibt
 * frei. Die sieben Segmente unter der Bühne sind zugleich Fortschritt und
 * Kapitel-Navigation (Klick = Sprung zum Akt, Hover vergrößert die Titel).
 * Mobile / reduced-motion: keine Bühne, keine Mocks — die sieben Schritte
 * als Textliste (auf Desktop dieselbe Liste als Screenreader-Fassung).
 */

const sectionRef = ref<HTMLElement | null>(null)

/* Headline-Blur-Reveal + Lead wie bei allen anderen Sections */
useReveal(sectionRef)

const captions = [
  'A member needs help.',
  'One click on the panel.',
  'The category asks the right questions.',
  'Their DM becomes a forum post.',
  'Stripe answers before your team does.',
  'Every reply crosses over, both ways.',
  'Closed, with transcript, reopen and rating.'
]

/* Mobile / reduced-motion: die Story als Liste — keine Bühne, keine Mocks */
const steps = [
  { title: 'Panel', text: 'A member picks a category on your panel, or simply sends the bot a DM.' },
  { title: 'Questions', text: 'A short form asks what this category needs: the Stripe order ID and what went wrong.' },
  { title: 'Ticket opened', text: 'The member gets a “Ticket Opened” card in their DMs and keeps talking there.' },
  { title: 'Forum post', text: 'Your team gets a tagged forum post with the member’s answers already on it.' },
  { title: 'Context first', text: 'Payment, customer and product from Stripe are on the thread before anyone replies.' },
  { title: 'Relay', text: 'Team replies arrive in the member’s DM. Their answers show up in the thread under their own name.' },
  { title: 'Closed', text: 'The member gets one card with the transcript, a reopen button and a rating.' }
]

/* Kapitel der Navigation — ein Titel pro Akt der Timeline */
const chapters = ['Request', 'Panel', 'Questions', 'Split', 'Context', 'Relay', 'Closed']

/* Akt-Startzeiten in Sekunden (Autoplay-Timeline) */
const ACT = { a0: 0, a1: 4, a2: 8, a3: 14, a4: 22, a5: 28, a6: 39 }
const ACT_END = 48.4 // letztes Segment ist voll, wenn die CLOSED-Buttons stehen
const actBounds = [...Object.values(ACT), ACT_END]

const activeAct = ref(0)
/* Pause/Play (WCAG 2.2.2: bewegte Inhalte > 5 s brauchen eine Stopp-Option) */
const storyPaused = ref(false)
/* Desktop: die zeitgesteuerte Bühne ist für AT dekorativ — die Erzählung
   steht als Liste (.hiw-steps, auf Desktop sr-only) daneben */
const stageDecorative = ref(false)

/* Erst in der Desktop-Timeline belegt; die Template-Handler bleiben stabil */
let seekAct: (i: number) => void = () => {}
let togglePlay: () => void = () => {}
function onChapterClick(i: number) {
  seekAct(i)
}
function onTogglePlay() {
  togglePlay()
}

const ORDER_NO = '4821-STRIPE'
const PROBLEM = 'Premium role missing after payment'
const FOOTER = 'Clarify · Efficient Ticketing'

const forumTags = [
  { label: 'Billing', emoji: '💳' },
  { label: 'Tech', emoji: '🔧' },
  { label: 'General', emoji: '💬' }
]

/* Die STARTED-Karte trägt die Antworten aus dem Modal als Name → Wert */
const startedFields = [
  { name: '👤 User', value: '@jonas.' },
  { name: 'Stripe order ID', value: ORDER_NO },
  { name: 'What happened?', value: PROBLEM }
]

const stripeFields = [
  { name: 'Status', value: '✅ succeeded' },
  { name: 'Amount', value: '€9.99' },
  { name: 'Customer', value: 'jonas@mail.de' },
  { name: 'Description', value: 'Premium (1 month)' }
]

let ctx: gsap.Context | undefined

onMounted(async () => {
  await nextTick()
  gsap.registerPlugin(ScrollTrigger)

  ctx = gsap.context((self) => {
    const q = self.selector!
    const el = (sel: string) => q(sel)[0] as HTMLElement | undefined
    const mm = gsap.matchMedia()

    /* ══ Desktop: zeitbasierte Autoplay-Timeline (Loop) ═════════════════════ */
    mm.add('(min-width: 1000px) and (prefers-reduced-motion: no-preference)', () => {
      /* ── Mess-Helfer ─────────────────────────────────────────────────────── */
      const layer = () => el('.hiw-ghost-layer')!.getBoundingClientRect()
      const centerOf = (sel: string, fallback = { x: 0, y: 0 }) => {
        const target = el(sel)
        if (!target) return fallback
        const r = target.getBoundingClientRect()
        const l = layer()
        return { x: r.left + r.width / 2 - l.left, y: r.top + r.height / 2 - l.top }
      }

      /* Single-Ansicht: das linke Panel wird rechnerisch in die Bühnenmitte
         geschoben (function-based → wird beim Refresh neu gemessen). */
      const centerX = () => {
        const stage = el('.hiw-stage')
        const panel = el('.hiw-panel-left')
        if (!stage || !panel) return 0
        return stage.offsetWidth / 2 - (panel.offsetLeft + panel.offsetWidth / 2)
      }

      /* Fake-Scroll: Liste so verschieben, dass `msgSel` voll sichtbar ist */
      const scrollFor = (containerSel: string, msgSel: string) => () => {
        const c = el(containerSel)
        const m = el(msgSel)
        if (!c || !m) return 0
        return -Math.max(0, m.offsetTop + m.offsetHeight + 26 - c.clientHeight)
      }
      const dmScroll = (msgSel: string) => scrollFor('.hiw-view-dm .dc-dm-messages', msgSel)
      const threadScroll = (msgSel: string) => scrollFor('.hiw-thread-overlay .dc-thread-messages', msgSel)

      /* Platz für das neue ThreadCard: Rest-Zeilen starten hochgeschoben */
      const slotShift = () => {
        const card = el('.hiw-thread-card')
        return card ? -(card.offsetHeight + 10) : -128
      }

      const tl = gsap.timeline({
        paused: true,
        repeat: -1,
        repeatDelay: 2.6,
        defaults: { ease: 'power2.inOut' },
        onUpdate: () => syncBar()
      })

      /* ── Initialzustände ─────────────────────────────────────────────────
         Doppelt gesetzt: sofort (erster Paint) UND als tl.set() bei 0, damit
         jede Loop-Wiederholung und jeder Kapitel-Sprung zurück sauber vom
         Anfangszustand startet. Explizit statt tl.from(): from-Tweens lesen
         ihre Endwerte beim Re-Init aus dem DOM (nach Resize = Zwischenwerte). */
      const init = (sel: string, vars: gsap.TweenVars) => {
        gsap.set(q(sel), vars)
        tl.set(q(sel), vars, 0)
      }
      init('.hiw-panel-left', { autoAlpha: 1, x: centerX, scale: 1.05, transformOrigin: '50% 50%' })
      init('.hiw-panel-right', { autoAlpha: 0, x: 56, y: 0, scale: 1 })
      init('.hiw-view-channel', { autoAlpha: 1, x: 0 })
      init('.hiw-view-dm', { autoAlpha: 0, x: 18 })
      init('.hiw-modal-layer', { autoAlpha: 0 })
      init('.hiw-modal-layer .dc-modal', { scale: 0.94, y: 10 })
      init('.hiw-modal-layer .dc-modal-header, .hiw-modal-layer .dc-modal-footer', { scaleY: 1, autoAlpha: 1 })
      init('.hiw-modal-layer .dc-modal-label', { autoAlpha: 1 })
      init('.hiw-msg-confirm, .hiw-msg-relay-in, .hiw-msg-user-reply, .hiw-msg-closed', { autoAlpha: 0, y: 12 })
      init('.hiw-msg-closed .dc-btn', { autoAlpha: 0, scale: 0.85 })
      init('.hiw-panel-right .dc-forum-header, .hiw-panel-right .dc-forum-toolbar, .hiw-panel-right .dc-forum-tagbar', { autoAlpha: 0, y: -10 })
      init('.hiw-thread-card', { autoAlpha: 0, clipPath: 'inset(0% 100% 0% 0%)' })
      init('.hiw-thread-card .dc-tag', { autoAlpha: 0, scale: 0.5 })
      init('.hiw-forum-rest', { y: slotShift })
      init('.hiw-thread-overlay', { autoAlpha: 0, y: 18 })
      init('.hiw-msg-opening, .hiw-msg-stripe, .hiw-msg-agent, .hiw-msg-user-mirror', { autoAlpha: 0, y: 12 })
      init('.hiw-msg-stripe .dc-card-field', { autoAlpha: 0, y: 8 })
      init('.hiw-fetch-skeleton, .hiw-typing-agent', { autoAlpha: 0 })
      init('.hiw-view-dm .dc-scroll, .hiw-thread-overlay .dc-scroll', { y: 0 })
      // Cursor-Startposition explizit im Loop-Start: fromTo-Tweens mit x/y nur
      // im from-Teil setzen beim Repeat-Wrap auf den Vor-Tween-Wert (0/0) zurück
      init('.hiw-cursor', {
        autoAlpha: 0,
        scale: 1,
        x: () => centerOf('.hiw-view-channel').x + 140,
        y: () => centerOf('.hiw-view-channel').y + 170
      })
      init('.hiw-click-ring', { autoAlpha: 0, scale: 0.4 })
      init('.hiw-caption-line', { yPercent: 115 })
      init('.hiw-caption:first-child .hiw-caption-line', { yPercent: 0 })

      /* Typewriter: SSR liefert den vollen Wert, die Timeline tippt ihn selbst
         (und leert ihn zu Beginn jeder Wiederholung) */
      const valueEl = el('.hiw-modal-layer .dc-modal-value-typed')
      const typing = { n: 0 }
      const resetTyping = () => {
        typing.n = 0
        if (valueEl) valueEl.textContent = ''
      }
      resetTyping()
      tl.call(resetTyping, [], 0)

      /* ── Kapitel-Navigation ──────────────────────────────────────────────
         Jedes Segment füllt sich linear über seinen Akt (transform-only via
         quickSetter), der Zähler und das aktive Kapitel springen an der
         Akt-Grenze um — Vue-Reaktivität nur bei echtem Wechsel. */
      const fillSetters = gsap.utils.toArray<HTMLElement>(q('.hiw-progress-fill'))
        .map(fill => gsap.quickSetter(fill, 'scaleX') as (v: number) => void)
      const counterEl = el('.hiw-step-n')
      const syncBar = () => {
        const t = tl.time()
        let idx = 0
        for (let i = 0; i < fillSetters.length; i++) {
          const from = actBounds[i]!
          const to = actBounds[i + 1]!
          if (t >= from) idx = i
          fillSetters[i]!(gsap.utils.clamp(0, 1, (t - from) / (to - from)))
        }
        if (activeAct.value !== idx) {
          activeAct.value = idx
          if (counterEl) counterEl.textContent = String(idx + 1).padStart(2, '0')
        }
      }
      seekAct = (i: number) => {
        const t = actBounds[i]
        if (t === undefined) return
        if (storyPaused.value) tl.time(t)
        else tl.play(t)
      }

      /* Dock-Magnification (Apple-Prinzip): die Kapitel-Titel wachsen
         abstandsabhängig zum Cursor — der nächste am stärksten, die Nachbarn
         weich mit. quickTo hält das pro Pointer-Frame billig; 'scale' ist eine
         Sammel-Property, daher scaleX/scaleY getrennt. */
      const bar = el('.hiw-chapters')
      const labels = gsap.utils.toArray<HTMLElement>(q('.hiw-chapter-label'))
      gsap.set(labels, { transformOrigin: '0% 100%' })
      const quick = (prop: string) => labels.map(l => gsap.quickTo(l, prop, { duration: 0.35, ease: 'power3.out' }))
      const scaleXTo = quick('scaleX')
      const scaleYTo = quick('scaleY')
      const liftTo = quick('y')
      const REACH = 250 // px — ab hier bleibt ein Titel unbeeinflusst (Segmente ~180px)
      const magnify = (e: PointerEvent) => {
        labels.forEach((label, i) => {
          // Segment-Kante (untransformiert) + Titelbreite aus dem Layout
          const left = (label.parentElement as HTMLElement).getBoundingClientRect().left
          const d = Math.min(Math.abs(e.clientX - (left + label.offsetWidth / 2)) / REACH, 1)
          const k = 1 - d * d
          scaleXTo[i]!(1 + 0.32 * k)
          scaleYTo[i]!(1 + 0.32 * k)
          liftTo[i]!(-5 * k)
        })
      }
      const relax = () => labels.forEach((_, i) => {
        scaleXTo[i]!(1)
        scaleYTo[i]!(1)
        liftTo[i]!(0)
      })
      bar?.addEventListener('pointermove', magnify, { passive: true })
      bar?.addEventListener('pointerleave', relax)

      /* ── Choreografie-Helfer ─────────────────────────────────────────────── */
      const captionEls = gsap.utils.toArray<HTMLElement>(q('.hiw-caption-line'))
      let activeCaption = 0
      const caption = (i: number, at: string | number) => {
        const prev = captionEls[activeCaption]
        const next = captionEls[i]
        if (prev && prev !== next) tl.to(prev, { yPercent: -115, duration: 0.5 }, at)
        if (next) tl.fromTo(next, { yPercent: 115 }, { yPercent: 0, duration: 0.6, ease: 'power3.out', immediateRender: false }, `${at}+=0.18`)
        activeCaption = i
      }

      const click = (targetSel: string, at: string | number) => {
        tl.set(q('.hiw-click-ring'), {
          x: () => centerOf(targetSel).x,
          y: () => centerOf(targetSel).y
        }, at)
        tl.to(q('.hiw-cursor'), { scale: 0.82, duration: 0.14, ease: 'power2.in' }, at)
        tl.to(q('.hiw-cursor'), { scale: 1, duration: 0.2, ease: 'power2.out' }, '>')
        tl.fromTo(q('.hiw-click-ring'), { autoAlpha: 0.9, scale: 0.4 }, { autoAlpha: 0, scale: 1.5, duration: 0.5, ease: 'power2.out', immediateRender: false }, at)
        tl.to(q(targetSel), { scale: 0.96, duration: 0.14, ease: 'power2.in' }, at)
        tl.to(q(targetSel), { scale: 1, duration: 0.22, ease: 'power2.out' }, '>')
      }

      /* ════════════════════════════════════════════════════════════════════
         AKT 0 (0–4): Single-Ansicht, der Cursor erscheint
         ════════════════════════════════════════════════════════════════════ */
      tl.addLabel('a0', ACT.a0)
      tl.to(q('.hiw-cursor'), { autoAlpha: 1, duration: 0.5 }, 1.2)
      /* ════════════════════════════════════════════════════════════════════
         AKT 1 (4–8): Ein Klick auf den Kategorie-Button des Panels
         ════════════════════════════════════════════════════════════════════ */
      tl.addLabel('a1', ACT.a1)
      caption(1, 'a1')
      tl.to(q('.hiw-cursor'), {
        x: () => centerOf('.hiw-ticket-btn').x + 6,
        y: () => centerOf('.hiw-ticket-btn').y + 8,
        duration: 1.4
      }, 'a1+=0.3')
      click('.hiw-ticket-btn', 'a1+=2.0')

      /* ════════════════════════════════════════════════════════════════════
         AKT 2 (8–14): Das Modal der Kategorie, Typewriter tippt die Order-ID
         ════════════════════════════════════════════════════════════════════ */
      tl.addLabel('a2', ACT.a2)
      caption(2, 'a2')
      tl.to(q('.hiw-modal-layer'), { autoAlpha: 1, duration: 0.5, ease: 'power2.out' }, 'a2')
      tl.to(q('.hiw-modal-layer .dc-modal'), { scale: 1, y: 0, duration: 0.6, ease: 'power2.out' }, 'a2')
      // Zeichenweises Tippen — deterministisch, auch bei Kapitel-Sprüngen rückwärts
      tl.to(typing, {
        n: ORDER_NO.length,
        duration: 2.2,
        ease: 'none',
        onUpdate: () => {
          if (valueEl) valueEl.textContent = ORDER_NO.slice(0, Math.round(typing.n))
        }
      }, 'a2+=0.9')
      tl.to(q('.hiw-cursor'), {
        x: () => centerOf('.hiw-modal-layer .dc-modal-submit').x + 4,
        y: () => centerOf('.hiw-modal-layer .dc-modal-submit').y + 6,
        duration: 0.9
      }, 'a2+=3.3')
      click('.hiw-modal-layer .dc-modal-submit', 'a2+=4.4')
      tl.to(q('.hiw-cursor'), { autoAlpha: 0, duration: 0.35 }, 'a2+=4.9')

      /* ════════════════════════════════════════════════════════════════════
         AKT 3 (14–22): DER SPLIT — Modal → DM-Karte + Forum-Post
         ════════════════════════════════════════════════════════════════════ */
      tl.addLabel('a3', ACT.a3)
      caption(3, 'a3')

      // Modal faltet sich: Header/Footer klappen weg, Hülle löst sich auf
      tl.to(q('.hiw-modal-layer .dc-modal-header'), { scaleY: 0.4, autoAlpha: 0, transformOrigin: '50% 0%', duration: 0.5 }, 'a3')
      tl.to(q('.hiw-modal-layer .dc-modal-footer'), { scaleY: 0.4, autoAlpha: 0, transformOrigin: '50% 100%', duration: 0.5 }, 'a3')
      tl.to(q('.hiw-modal-layer .dc-modal-label'), { autoAlpha: 0, duration: 0.35 }, 'a3+=0.1')
      tl.to(q('.hiw-modal-layer'), { autoAlpha: 0, duration: 0.5 }, 'a3+=0.55')

      // Parallel: Links wird aus dem Channel die DM (Frame bleibt stehen)
      tl.to(q('.hiw-panel-left'), { x: 0, scale: 1, duration: 2.4, ease: 'power3.inOut' }, 'a3+=0.7')
      tl.to(q('.hiw-view-channel'), { autoAlpha: 0, x: -18, duration: 0.8 }, 'a3+=1.4')
      tl.to(q('.hiw-view-dm'), { autoAlpha: 1, x: 0, duration: 0.8 }, 'a3+=1.7')
      tl.to(q('.hiw-msg-confirm'), { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power2.out' }, 'a3+=2.6')

      // Parallel: Rechts assembliert sich das Forum zeilenweise
      tl.to(q('.hiw-panel-right'), { autoAlpha: 1, x: 0, duration: 1.6, ease: 'power3.out' }, 'a3+=1.4')
      tl.to(q('.hiw-panel-right .dc-forum-header, .hiw-panel-right .dc-forum-toolbar, .hiw-panel-right .dc-forum-tagbar'), {
        autoAlpha: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.22,
        ease: 'power2.out'
      }, 'a3+=1.7')
      tl.to(q('.hiw-forum-rest'), { y: 0, duration: 1.0 }, 'a3+=2.6')

      // Der neue Post ENTROLLT sich an Ort und Stelle
      tl.set(q('.hiw-thread-card'), { autoAlpha: 1 }, 'a3+=3.7')
      tl.to(q('.hiw-thread-card'), { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.4, ease: 'power2.inOut' }, 'a3+=3.8')
      // Die Kategorie wird zum Forum-Tag
      tl.to(q('.hiw-thread-card .dc-tag'), { autoAlpha: 1, scale: 1, duration: 0.5, ease: 'back.out(2)' }, 'a3+=5.1')

      /* ════════════════════════════════════════════════════════════════════
         AKT 4 (22–28): Der Thread öffnet sich, der Stripe-Lookup landet
         ════════════════════════════════════════════════════════════════════ */
      tl.addLabel('a4', ACT.a4)
      caption(4, 'a4')
      tl.to(q('.hiw-panel-left'), { autoAlpha: 0.55, duration: 1.0 }, 'a4')
      tl.to(q('.hiw-thread-overlay'), { autoAlpha: 1, y: 0, duration: 0.9, ease: 'power2.out' }, 'a4+=0.3')
      tl.to(q('.hiw-msg-opening'), { autoAlpha: 1, y: 0, duration: 0.7, ease: 'power2.out' }, 'a4+=0.9')

      // Stripe-Lookup: erst Skeleton, dann baut sich die Karte Feld für Feld

      tl.to(q('.hiw-fetch-skeleton'), { autoAlpha: 1, duration: 0.35 }, 'a4+=2.0')
      tl.to(q('.hiw-fetch-skeleton'), { autoAlpha: 0, duration: 0.3 }, 'a4+=3.0')
      tl.to(q('.hiw-msg-stripe'), { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power2.out' }, 'a4+=3.2')
      tl.to(q('.hiw-msg-stripe .dc-card-field'), { autoAlpha: 1, y: 0, duration: 0.5, stagger: 0.2, ease: 'power2.out' }, 'a4+=3.5')
      tl.to(q('.hiw-thread-overlay .dc-scroll'), { y: threadScroll('.hiw-msg-stripe'), duration: 0.9 }, 'a4+=3.2')

      /* ════════════════════════════════════════════════════════════════════
         AKT 5 (28–39): Antworten fließen — in beide Richtungen
         ════════════════════════════════════════════════════════════════════ */
      tl.addLabel('a5', ACT.a5)
      caption(5, 'a5')

      // Team antwortet im Thread …
      tl.to(q('.hiw-typing-agent'), { autoAlpha: 1, duration: 0.4 }, 'a5+=0.2')
      tl.to(q('.hiw-typing-agent'), { autoAlpha: 0, duration: 0.3 }, 'a5+=1.4')
      tl.to(q('.hiw-msg-agent'), { autoAlpha: 1, y: 0, duration: 0.7, ease: 'power2.out' }, 'a5+=1.6')
      tl.to(q('.hiw-thread-overlay .dc-scroll'), { y: threadScroll('.hiw-msg-agent'), duration: 0.8 }, 'a5+=1.6')
      tl.to(q('.hiw-panel-left'), { autoAlpha: 1, duration: 0.8 }, 'a5+=2.2')

      // … der Bot stellt die Antwort in die DM
      tl.to(q('.hiw-view-dm .dc-scroll'), { y: dmScroll('.hiw-msg-relay-in'), duration: 0.8 }, 'a5+=3.4')
      tl.to(q('.hiw-msg-relay-in'), { autoAlpha: 1, y: 0, duration: 0.7, ease: 'power2.out' }, 'a5+=4.4')

      // Der Member antwortet in der DM — der Webhook spiegelt die Nachricht
      // unter seinem Namen in den Thread
      tl.to(q('.hiw-msg-user-reply'), { autoAlpha: 1, y: 0, duration: 0.7, ease: 'power2.out' }, 'a5+=5.8')
      tl.to(q('.hiw-view-dm .dc-scroll'), { y: dmScroll('.hiw-msg-user-reply'), duration: 0.7 }, 'a5+=5.8')
      tl.to(q('.hiw-thread-overlay .dc-scroll'), { y: threadScroll('.hiw-msg-user-mirror'), duration: 0.8 }, 'a5+=7.9')
      tl.to(q('.hiw-msg-user-mirror'), { autoAlpha: 1, y: 0, duration: 0.7, ease: 'power2.out' }, 'a5+=8.8')

      /* ════════════════════════════════════════════════════════════════════
         AKT 6 (39–50): Finale — Close, Rück-Morph zur Single-DM, CLOSED-Karte
         ════════════════════════════════════════════════════════════════════ */
      tl.addLabel('a6', ACT.a6)
      caption(6, 'a6')

      // Cursor kehrt zurück und schließt das Ticket
      tl.set(q('.hiw-cursor'), {
        x: () => centerOf('.hiw-close-btn').x + 90,
        y: () => centerOf('.hiw-close-btn').y + 70
      }, 'a6')
      tl.to(q('.hiw-cursor'), { autoAlpha: 1, duration: 0.4 }, 'a6')
      tl.to(q('.hiw-cursor'), {
        x: () => centerOf('.hiw-close-btn').x + 5,
        y: () => centerOf('.hiw-close-btn').y + 7,
        duration: 0.8
      }, 'a6+=0.4')
      click('.hiw-close-btn', 'a6+=1.3')
      tl.to(q('.hiw-cursor'), { autoAlpha: 0, duration: 0.3 }, 'a6+=1.8')

      // Thread schließt sich und wird archiviert — der Post dimmt im Forum
      tl.to(q('.hiw-thread-overlay'), { autoAlpha: 0, y: 18, duration: 0.8 }, 'a6+=1.9')
      tl.to(q('.hiw-thread-card'), { autoAlpha: 0.6, duration: 0.6 }, 'a6+=2.4')

      // Das Forum sinkt nach unten weg
      tl.to(q('.hiw-panel-right'), { autoAlpha: 0, scale: 0.92, y: 26, transformOrigin: '50% 100%', duration: 1.4, ease: 'power3.in' }, 'a6+=3.4')

      // Palindrom: die DM gleitet zurück in die Bühnenmitte
      tl.to(q('.hiw-panel-left'), { x: centerX, scale: 1.05, duration: 2.0, ease: 'power3.inOut' }, 'a6+=4.2')

      // CLOSED-Karte + die drei Buttons: Reopen · Transcript · Feedback
      tl.to(q('.hiw-msg-closed'), { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power2.out' }, 'a6+=6.0')
      tl.to(q('.hiw-view-dm .dc-scroll'), { y: dmScroll('.hiw-msg-closed'), duration: 0.9 }, 'a6+=6.0')
      tl.to(q('.hiw-msg-closed .dc-btn'), { autoAlpha: 1, scale: 1, duration: 0.5, stagger: 0.25, ease: 'back.out(2)' }, 'a6+=6.9')
      tl.to({}, { duration: 1.6 }) // Atem-Beat, dann repeatDelay → Loop von vorn

      // Tempo: die Choreografie bleibt in Timeline-Sekunden notiert (Akt-
      // Grenzen, Kapitel-Sprünge), abgespielt wird sie 1.7× schneller
      tl.timeScale(1.7)

      /* Autoplay-Steuerung: läuft, solange die Bühne im Viewport ist — beim
         Rausscrollen pausiert sie, beim Reinscrollen läuft sie weiter. Kein
         Pin, kein Scrub: der Seiten-Scroll bleibt frei. */
      const playState = ScrollTrigger.create({
        trigger: el('.hiw-stage'),
        start: 'top 85%',
        end: 'bottom 15%',
        onToggle: self => (self.isActive && !storyPaused.value ? tl.play() : tl.pause())
      })
      togglePlay = () => {
        storyPaused.value = !storyPaused.value
        if (storyPaused.value) tl.pause()
        else if (playState.isActive) tl.play()
      }
      stageDecorative.value = true

      /* Nach Layout-Änderungen (Resize, Font-Swap) die function-based Werte
         neu messen: zurück auf 0 (alle Startzustände), invalidieren, dann zur
         alten Zeit vorspulen — so initialisiert jeder Tween aus dem korrekten
         Vorgänger-Zustand statt aus einem halbfertigen Zwischenstand. */
      const onRefresh = () => {
        const t = tl.time()
        tl.time(0).invalidate().time(t)
      }
      ScrollTrigger.addEventListener('refresh', onRefresh)

      /* Entrance der Bühne: steigt einmalig ein, die Story läuft dann
         zeitbasiert weiter */
      gsap.from(q('.hiw-scene'), {
        opacity: 0,
        y: 40,
        duration: 1.1,
        ease: 'power3.out',
        clearProps: 'transform',
        scrollTrigger: { trigger: el('.hiw-scene'), start: 'top 85%', once: true }
      })

      return () => {
        bar?.removeEventListener('pointermove', magnify)
        bar?.removeEventListener('pointerleave', relax)
        ScrollTrigger.removeEventListener('refresh', onRefresh)
        playState.kill()
        tl.kill()
        if (valueEl) valueEl.textContent = ORDER_NO
        seekAct = () => {}
        togglePlay = () => {}
        stageDecorative.value = false
      }
    })

    return () => mm.revert()
  }, sectionRef.value!)
})

onBeforeUnmount(() => ctx?.revert())
</script>

<template>
  <section id="funktionsweise" ref="sectionRef" class="hiw">
    <span class="section-rule" aria-hidden="true" />
    <div class="hiw-body">
      <div class="section-shell hiw-inner">
        <!-- Head -->
        <div class="hiw-head">
          <h2 class="h-section" data-split>One ticket, two windows, one relay in between.</h2>
          <p class="copy-muted hiw-lead reveal">
            The member never leaves their DMs. Your team never leaves the forum.
            Clarify carries every message across and adds the context your team needs to answer.
          </p>
        </div>

        <!-- Akt-Caption: eine maskierte Zeile, wechselt pro Akt; rechts Zähler + Pause -->
        <div class="hiw-caption-rail">
          <div class="hiw-captions" aria-hidden="true">
            <div v-for="c in captions" :key="c" class="hiw-caption">
              <span class="hiw-caption-line">{{ c }}</span>
            </div>
          </div>
          <div class="hiw-caption-tools">
            <span class="hiw-step-counter" aria-hidden="true"><span class="hiw-step-n">01</span> / {{ String(captions.length).padStart(2, '0') }}</span>
            <button
              type="button"
              class="hiw-play"
              :aria-pressed="storyPaused"
              :aria-label="storyPaused ? 'Play story' : 'Pause story'"
              :title="storyPaused ? 'Play' : 'Pause'"
              @click="onTogglePlay"
            >
              <UIcon :name="storyPaused ? 'i-lucide-play' : 'i-lucide-pause'" class="hiw-play-icon" />
            </button>
          </div>
        </div>

        <!-- Szene: Bühne + Fortschritt -->
        <div class="hiw-scene">
          <div class="hiw-stage" :aria-hidden="stageDecorative || undefined">
            <!-- ── LINKS: die Member-Perspektive (Channel → DM) ──────────── -->
            <div class="hiw-panel hiw-panel-left">
              <div class="hiw-views">
                <div class="hiw-view hiw-view-channel">
                  <DiscordChannelView channel="support" topic="Questions? Open a ticket below." placeholder="Message #support">
                    <DiscordMessage author="Clarify" bot time="Today at 9:02 AM">
                      <!-- CREATION-Blueprint: ein Button pro Kategorie (Button-Modus) -->
                      <DiscordCard heading="Create a ticket" :footer="FOOTER" thumbnail>
                        Pick the category that fits your question. Your ticket opens right here in your DMs — no new channels, no pings.
                        <template #actions>
                          <DiscordButton class="hiw-ticket-btn" label="Billing" variant="primary" emoji="💳" />
                          <DiscordButton label="Tech" variant="secondary" emoji="🔧" />
                          <DiscordButton label="General" variant="secondary" emoji="💬" />
                        </template>
                      </DiscordCard>
                    </DiscordMessage>
                  </DiscordChannelView>
                  <div class="hiw-modal-layer">
                    <!-- Das Modal der Kategorie: Titel = Kategorie, Felder = ihre Inputs -->
                    <DiscordModal
                      title="Billing"
                      :fields="[
                        { label: 'Stripe order ID', placeholder: 'e.g. 4821-STRIPE', value: ORDER_NO },
                        { label: 'What happened?', value: PROBLEM, paragraph: true, required: false }
                      ]"
                      submit-label="Submit"
                      cancel-label="Cancel"
                    />
                  </div>
                </div>

                <div class="hiw-view hiw-view-dm">
                  <DiscordDmView user="Clarify" bot placeholder="Message @Clarify">
                    <DiscordDateDivider label="July 5, 2026" />
                    <!-- OPENING-Blueprint, so wie der Bot ihn in die DM stellt -->
                    <DiscordMessage class="hiw-msg-confirm" author="Clarify" bot time="Today at 2:31 PM">
                      <DiscordCard
                        heading="Ticket Opened"
                        :fields="[{ name: '🎫 Ticket', value: '#4821 · Billing' }]"
                        :footer="FOOTER"
                        thumbnail
                      >
                        <strong>Please wait for support to respond.</strong><br>
                        <em>This typically takes 15 minutes to 4 hours.</em>
                      </DiscordCard>
                    </DiscordMessage>
                    <!-- Relay: die Team-Antwort kommt über den Bot, mit Namen -->
                    <DiscordMessage class="hiw-msg-relay-in" author="Clarify" bot time="Today at 2:38 PM">
                      <strong>Lena&nbsp;|&nbsp;Support:</strong> Payment verified ✅ The role was stuck on our side —
                      I assigned it manually. Mind taking a quick look? 🙌
                    </DiscordMessage>
                    <DiscordMessage class="hiw-msg-user-reply" author="jonas." avatar-color="#e8a33d" time="Today at 2:39 PM">
                      Perfect, it works. Thank you! 🙏
                    </DiscordMessage>
                    <!-- CLOSED-Blueprint + die drei Lifecycle-Buttons -->
                    <DiscordMessage class="hiw-msg-closed" author="Clarify" bot time="Today at 2:45 PM">
                      <DiscordCard accent="var(--dc-dark-red)" :footer="FOOTER">
                        Ticket has been closed.
                        <template #actions>
                          <DiscordButton label="Reopen" variant="secondary" emoji="🔓" />
                          <DiscordButton label="Transcript" variant="secondary" emoji="📑" link />
                          <DiscordButton label="Feedback" variant="primary" emoji="⭐" />
                        </template>
                      </DiscordCard>
                    </DiscordMessage>
                  </DiscordDmView>
                </div>
              </div>
            </div>

            <!-- ── RECHTS: die Team-Perspektive (Forum → Thread) ─────────── -->
            <div class="hiw-panel hiw-panel-right">
              <div class="hiw-views">
                <DiscordForumView channel="support-tickets" :tags="forumTags">
                  <!-- Thread-Name folgt dem Default-Muster %user_name%-%tag_name% -->
                  <DiscordThreadCard
                    class="hiw-thread-card"
                    title="jonas.-Billing"
                    author="Clarify"
                    avatar-color="#e8a33d"
                    :tags="[{ label: 'Billing', emoji: '💳' }]"
                    :messages="4"
                    activity="just now"
                    highlighted
                  />
                  <div class="hiw-forum-rest">
                    <div class="hiw-forum-rest-card">
                      <DiscordThreadCard
                        title="mira-Tech"
                        author="Clarify"
                        avatar-color="#3ba55c"
                        :tags="[{ label: 'Tech', emoji: '🔧' }]"
                        :messages="7"
                        activity="25m ago"
                      />
                    </div>
                    <div class="hiw-forum-rest-card hiw-forum-rest-dim">
                      <DiscordThreadCard
                        title="tom_k-General"
                        author="Clarify"
                        avatar-color="#5865f2"
                        :tags="[{ label: 'General', emoji: '💬' }]"
                        :messages="2"
                        activity="1h ago"
                      />
                    </div>
                  </div>
                </DiscordForumView>

                <div class="hiw-thread-overlay">
                  <DiscordThreadView title="jonas.-Billing">
                    <DiscordDateDivider label="July 5, 2026" />
                    <!-- STARTED-Blueprint: die Modal-Antworten als Felder + Close/Assign -->
                    <DiscordMessage class="hiw-msg-opening" author="Clarify" bot time="Today at 2:31 PM">
                      <DiscordCard heading="Ticket has been started" :fields="startedFields" :footer="FOOTER" thumbnail>
                        <template #actions>
                          <DiscordButton class="hiw-close-btn" label="Close" variant="danger" emoji="🔒" />
                          <DiscordButton label="Assign" variant="secondary" emoji="🔗" />
                        </template>
                      </DiscordCard>
                    </DiscordMessage>
                    <div class="hiw-fetch-skeleton" aria-hidden="true">
                      <span class="hiw-skel hiw-skel-1" />
                      <span class="hiw-skel hiw-skel-2" />
                    </div>
                    <!-- Stripe-Info-Karte: Lookup über den Stripe-Input beim Öffnen -->
                    <DiscordMessage class="hiw-msg-stripe" author="Clarify" bot compact>
                      <DiscordCard
                        heading="Stripe Payment"
                        subheading="pi_3Q8k…4821 · live"
                        accent="#635bff"
                        :fields="stripeFields"
                      >
                        <template #actions>
                          <DiscordButton label="View in Stripe" variant="secondary" link />
                          <DiscordButton label="Receipt" variant="secondary" link />
                        </template>
                      </DiscordCard>
                    </DiscordMessage>
                    <DiscordMessage class="hiw-msg-agent" author="Lena | Support" avatar-color="#9b59b6" time="Today at 2:38 PM">
                      Payment verified ✅ The role was stuck on our side — I assigned it manually. Mind taking a quick look? 🙌
                    </DiscordMessage>
                    <!-- Die Member-Antwort kommt per Webhook unter seinem Namen an -->
                    <DiscordMessage class="hiw-msg-user-mirror" author="jonas." webhook avatar-color="#e8a33d" time="Today at 2:39 PM">
                      Perfect, it works. Thank you! 🙏
                    </DiscordMessage>
                    <template #typing>
                      <DiscordTyping class="hiw-typing-agent" name="Lena | Support" />
                    </template>
                  </DiscordThreadView>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Fortschritt + Kapitel-Navigation: ein Segment pro Akt ─────── -->
          <div class="hiw-chapters" role="group" aria-label="Story chapters">
            <button
              v-for="(c, i) in chapters"
              :key="c"
              type="button"
              class="hiw-chapter"
              :class="{ 'is-active': activeAct === i, 'is-done': activeAct > i }"
              :aria-current="activeAct === i ? 'step' : undefined"
              @click="onChapterClick(i)"
            >
              <span class="hiw-progress-seg" aria-hidden="true">
                <span class="hiw-progress-fill" />
              </span>
              <span class="hiw-chapter-label">{{ c }}</span>
            </button>
          </div>

          <!-- Cursor-Overlay -->
          <div class="hiw-ghost-layer" aria-hidden="true">
            <span class="hiw-click-ring will-transform" />
            <svg class="hiw-cursor will-transform" width="22" height="22" viewBox="0 0 24 24">
              <path d="M5.5 3.2 19 11.5l-6.3 1.2 3.4 6.3-2.6 1.4-3.4-6.3-4.6 4.5z" fill="#fff" stroke="#111" stroke-width="1.4" stroke-linejoin="round" />
            </svg>
          </div>
        </div>

        <!-- Mobile / reduced-motion: keine Bühne, keine Mocks — die Schritte als
             Liste. Auf Desktop dieselbe Liste als Screenreader-Fassung (sr-only). -->
        <ol class="hiw-steps shell-gutter">
          <li v-for="(st, i) in steps" :key="st.title" class="hiw-step">
            <span class="hiw-step-no">{{ String(i + 1).padStart(2, '0') }}</span>
            <div class="hiw-step-body">
              <span class="hiw-step-title">{{ st.title }}</span>
              <p class="hiw-step-text copy-muted">{{ st.text }}</p>
            </div>
          </li>
        </ol>
      </div>
    </div>
  </section>
</template>

<style scoped>
.hiw {
  position: relative;
  padding-top: var(--section-gap);
  /* Luft unter der Kapitel-Leiste bis zum nächsten Section-Divider */
  padding-bottom: clamp(64px, 7vw, 104px);
}

/* Kein Pin mehr: normaler Block im Seitenfluss, die Story spielt von selbst */
.hiw-body {
  position: relative;
}

/* Atmosphäre hinter der Bühne — die Sektion steht nicht im leeren Schwarz */
.hiw-body::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(52% 46% at 50% 54%, var(--nb-accent-tint), transparent 72%);
  opacity: 0.55;
  pointer-events: none;
}

.hiw-inner {
  display: flex;
  flex-direction: column;
  gap: 26px; /* Luft Überschrift ↔ Caption ↔ Bühne — spiegelt den Baseline-Abstand */
}

/* Text sitzt im Gutter, nie direkt auf der Rail */
.hiw-head,
.hiw-caption-rail {
  padding-inline: var(--nb-gutter);
}

.hiw-head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

.hiw-head::before {
  content: '';
  width: 36px;
  height: 2px;
  background: var(--nb-accent-soft);
}

.hiw-head .h-section {
  font-size: clamp(1.9rem, 2.6vw, 2.6rem);
}

.hiw-lead {
  max-width: 62ch;
  font-size: 1.02rem;
}

/* ── Akt-Caption: eine Zeile, maskiert gewechselt — rechts Zähler + Pause ── */
.hiw-caption-rail {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-size: 1.02rem;
}

.hiw-captions {
  position: relative;
  flex: 1;
  min-width: 0;
  height: 1.7em;
}

.hiw-caption {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.hiw-caption-line {
  display: inline-block;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--nb-ink-muted);
}

/* ── Szene ───────────────────────────────────────────────────────────────── */
.hiw-scene {
  position: relative;
}

.hiw-stage {
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  align-items: stretch;
}

.hiw-panel {
  min-width: 0;
}

.hiw-views {
  position: relative;
}

.hiw-view {
  position: relative;
}

/* Modal-Layer über dem Channel */
.hiw-modal-layer {
  position: absolute;
  inset: 0;
  z-index: 10;
  border-radius: var(--radius-md);
  overflow: hidden;
}

/* ── Fortschritt + Kapitel: sieben Segmente, eines pro Akt — ruhig, ohne
   Licht. Jedes Segment ist ein Button (Sprung zum Akt), darunter sein Titel. */
.hiw-chapters {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  margin-top: 26px; /* Luft Bühne ↔ Fortschritt — gleicher Abstand wie zur Überschrift */
}

.hiw-chapter {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  padding: 10px 0 4px; /* großzügige Trefferfläche um die 2px-Linie */
  background: none;
  border: 0;
  cursor: pointer;
  color: var(--nb-ink-faint);
  text-align: left;
  transition: color var(--dur) var(--ease-out);
}

.hiw-progress-seg {
  position: relative;
  display: block;
  width: 100%;
  height: 2px;
  background: var(--nb-hairline-strong);
  overflow: hidden;
  transition:
    height var(--dur) var(--ease-out),
    background var(--dur) var(--ease-out);
}

.hiw-progress-fill {
  position: absolute;
  inset: 0;
  background: var(--nb-accent-soft);
  transform: scaleX(0);
  transform-origin: 0% 50%;
}

/* Titel: Transform fährt GSAP (abstandsabhängige Dock-Magnification) */
.hiw-chapter-label {
  display: inline-block;
  font-family: var(--font-mono);
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
  transform-origin: 0 100%;
}

.hiw-chapter.is-active {
  color: var(--nb-ink);
}

.hiw-chapter:hover {
  color: var(--nb-ink);
}

.hiw-chapter:hover .hiw-progress-seg {
  height: 4px;
  margin-block: -1px; /* wächst symmetrisch, ohne die Zeile zu verschieben */
  background: rgba(255, 255, 255, 0.24);
}

/* Zähler „02 / 07" + Pause rechts in der Caption-Zeile */
.hiw-caption-tools {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
}

.hiw-step-counter {
  font-family: var(--font-mono);
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  color: var(--nb-ink-faint);
  font-variant-numeric: tabular-nums;
}

.hiw-play {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--nb-hairline);
  color: var(--nb-ink-faint);
  cursor: pointer;
  transition:
    color var(--dur-fast) var(--ease-out),
    border-color var(--dur-fast) var(--ease-out),
    background var(--dur-fast) var(--ease-out);
}

.hiw-play:hover {
  color: var(--nb-ink);
  border-color: var(--nb-hairline-strong);
  background: var(--nb-accent-tint);
}

.hiw-play-icon {
  width: 13px;
  height: 13px;
}

/* ── Die sieben Schritte als Liste (Mobile / reduced-motion) ─────────────── */
.hiw-steps {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding-block: 0;
  list-style: none;
  border-top: 1px solid var(--nb-hairline);
}

.hiw-step {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 0;
  border-bottom: 1px solid var(--nb-hairline);
}

.hiw-step-no {
  flex-shrink: 0;
  padding-top: 3px;
  font-family: var(--font-mono);
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  color: var(--nb-accent-soft);
}

.hiw-step-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.hiw-step-title {
  font-size: 0.98rem;
  font-weight: 600;
  color: var(--nb-ink);
}

.hiw-step-text {
  font-size: 0.9rem;
  line-height: 1.5;
}

/* ── Cursor-Overlay ──────────────────────────────────────────────────────── */
.hiw-ghost-layer {
  position: absolute;
  inset: 0;
  z-index: 20;
  pointer-events: none;
}

.hiw-click-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 36px;
  height: 36px;
  margin: -18px 0 0 -18px;
  border-radius: 50%;
  border: 2px solid var(--nb-accent-soft);
  transform: translateZ(0);
}

.hiw-cursor {
  position: absolute;
  top: 0;
  left: 0;
  margin: -4px 0 0 -3px;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.55));
}

/* ── Forum-Zeilen unterhalb des neuen Tickets ────────────────────────────── */
.hiw-forum-rest {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.hiw-forum-rest-card {
  opacity: 0.8;
}

.hiw-forum-rest-dim {
  opacity: 0.55;
}

/* Thread-Overlay + Skeleton */
.hiw-thread-overlay {
  position: absolute;
  inset: 0;
  z-index: 4;
}

.hiw-thread-overlay :deep(.dc-thread) {
  height: 100%;
}

.hiw-fetch-skeleton {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 16px 2px 72px;
}

.hiw-skel {
  height: 10px;
  border-radius: var(--radius-pill);
  background: linear-gradient(90deg, var(--dc-bg-input) 25%, var(--dc-bg-hover) 50%, var(--dc-bg-input) 75%);
  background-size: 200% 100%;
  animation: hiw-shimmer 1.4s linear infinite;
}

.hiw-skel-1 {
  width: 62%;
}

.hiw-skel-2 {
  width: 44%;
}

@keyframes hiw-shimmer {
  from {
    background-position: 200% 0;
  }

  to {
    background-position: -200% 0;
  }
}

/* ── Desktop-Layout (deckungsgleich mit der gsap.matchMedia-Bedingung) ───── */
@media (min-width: 1000px) and (prefers-reduced-motion: no-preference) {
  .hiw-stage {
    grid-template-columns: 1fr 1fr;
    gap: 3%;
  }

  .hiw-views {
    height: 580px;
  }

  .hiw-view {
    position: absolute;
    inset: 0;
  }

  .hiw-view :deep(.dc-channel),
  .hiw-view :deep(.dc-dm),
  .hiw-panel-right :deep(.dc-forum) {
    height: 100%;
  }

  .hiw-panel-right :deep(.dc-forum) {
    position: absolute;
    inset: 0;
  }

  /* Die Liste bleibt als Screenreader-Fassung der Story im Dokument */
  .hiw-steps {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
}

/* ── Mobile ODER reduced-motion: keine Bühne, keine Mocks — nur die Liste ── */
@media (max-width: 999.98px), (prefers-reduced-motion: reduce) {
  .hiw-caption-rail,
  .hiw-scene {
    display: none;
  }
}
</style>
